import { useEffect, useState } from 'react';
import { Form, useNavigate } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useConfirm } from 'material-ui-confirm';
import dayjs from 'dayjs';

import { IBatch, IBatchPayeeOption } from '../../types/shipment';
import { ISelectOption } from '../../types/list';

import { AppConfig } from '../../config/appConfig';
import { BatchService } from '../../services/batchService';
import { ErrorProvider } from '../../classes/errorProvider';
import { useUserContext } from '../../contexts/userContext';
import { UtilityService } from '../../services/utilityService';

import Spinner from '../spinner';
import { Alert, Button, IconButton, InputLabel, MenuItem, Select, TextField } from '@mui/material';

import '../../styles/entity/batchForms.scss';

export interface IBatchDetailsForm {
	batchDetails: IBatch;
	updateBatchDetails: (batch: IBatch) => void;
	isLoadingDetails: boolean;
	payeeCache: IBatchPayeeOption[],
	updatePayeeCache: (payees: IBatchPayeeOption[]) => void;
}

export interface IFormFields {
	id?: number;
	invoiceNumber: string;
	selectedPayee: string;
	payeeType: string;
	payeeId: string;
	month: string; //Format: YYYYMM
	status: string;
}

const BatchDetailsForm: React.FunctionComponent<IBatchDetailsForm> = ({ batchDetails, updateBatchDetails, isLoadingDetails, payeeCache, updatePayeeCache }) => {
	const confirm = useConfirm();
	const navigate = useNavigate();
	const userContext = useUserContext();
	const [isSaving, setIsSaving] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [canEdit, setCanEdit] = useState(userContext?.permissions.canManageBatches);
	const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
	const [successMessage, setSuccessMessage] = useState<string | undefined>(undefined);
	const [formFields, setFormFields] = useState<IFormFields>({ ...batchDetails, ['selectedPayee']: "-1" });
	const [isLoadingDateOptions, setIsLoadingDateOptions] = useState(true);
	const [payeeOptions, setPayeeOptions] = useState<IBatchPayeeOption[]>([]);
	const [isLoadingPayees, setIsLoadingPayees] = useState(true);
	const [yearOptions, setYearOptions] = useState<ISelectOption[]>([]);
	const [selectedMonthOption, setSelectedMonthOption] = useState<string>(undefined);
	const [selectedYearOption, setSelectedYearOption] = useState<string>(undefined);
	const monthOptions: ISelectOption[] = [
		{ name: '01 - January', value: '01' },
		{ name: '02 - February', value: '02' },
		{ name: '03 - March', value: '03' },
		{ name: '04 - April', value: '04' },
		{ name: '05 - May', value: '05' },
		{ name: '06 - June', value: '06' },
		{ name: '07 - July', value: '07' },
		{ name: '08 - August', value: '08' },
		{ name: '09 - September', value: '09' },
		{ name: '10 - October', value: '10' },
		{ name: '11 - November', value: '11' },
		{ name: '12 - December', value: '12' },
	];
	

	useEffect(() => {
		loadDateFields();
	}, []);

	useEffect(() => {
		if (!isLoadingDateOptions && !isLoadingPayees) {

			// Don't allow setting a date in the future
			var newDate = dayjs().month(parseInt(selectedMonthOption) - 1).year(parseInt(selectedYearOption));
			if (newDate > dayjs()) {
				const safeMonth = dayjs().format('MM');
				const safeYear = dayjs().format('YYYY');
				setSelectedMonthOption(dayjs().format('MM'));
				setSelectedYearOption(dayjs().format('YYYY'));
				setFormFields({ ...formFields, ['month']: safeYear + safeMonth });
			}
			else {
				setFormFields({ ...formFields, ['month']: selectedYearOption + selectedMonthOption });
			}
		}
	}, [selectedMonthOption, selectedYearOption, isLoadingPayees])

	useEffect(() => {
		if (formFields?.month?.length == 6) {
			setSelectedYearOption(formFields.month.substring(0, 4));
			setSelectedMonthOption(formFields.month.substring(4, 6));
		}
	}, [formFields])

	useEffect(() => {
		if (!isLoadingDateOptions) {
			// Wait for loadDateFields() to finish so both load methods aren't competing to overwrite each other's formFields
			if (!payeeCache || payeeCache.length == 0) {
				loadPayees();
			}
			else {
				setPayeeOptions([...payeeCache]);
				let newFormFields = { ...formFields };
				newFormFields.selectedPayee = payeeCache.find((item) => item.entityId.toString() == newFormFields.payeeId)?.localId?.toString() || '-1';
				setFormFields(newFormFields);
				setIsLoadingPayees(false);
			}
		}
	}, [isLoadingDateOptions]);

	const loadDateFields = async () => {
		const startYear = 2025;
		const currentYear = parseInt(dayjs().format('YYYY'));
		const years: ISelectOption[] = [];
		for (let year = startYear; year <= currentYear; year++) {
			years.push({ name: year.toString(), value: year.toString() });
		}
		setYearOptions(years);
		setIsLoadingDateOptions(false);
	}

	const loadPayees = async () => {
		setIsLoadingPayees(true);
		BatchService.getPayees()
			.then((response) => {
				console.log('BatchService.getPayees: SUCCESS', response);
				let options: IBatchPayeeOption[] = [];
				response.data.map((item, index) => {
					let option: IBatchPayeeOption = {
						localId: index,
						entityId: item.id,
						entityName: item.name,
						entityType: item.type
					}

					options.push(option);
				})
				setPayeeOptions(options);
				if (options.length > 0) {
					let newFormFields = { ...formFields };
					newFormFields.selectedPayee = options.find((item) => item.entityId.toString() == newFormFields.payeeId)?.localId?.toString() || '-1';
					setFormFields(newFormFields);
				}

				if (updatePayeeCache) {
					updatePayeeCache(options);
				};
			})
			.catch((error) => {
				console.log('BatchService.getPayees: ERROR', error);
				setErrorMessage("We encountered an error while loading payees. Please try again.");
			})
			.finally(() => {
				setIsLoadingPayees(false);
			})
	}

	const setPayeeInfo = (selection: string) => {
		let newFormFields: IFormFields = { ...formFields };
		let selectedPayee = payeeOptions.filter((item) => { return item.localId.toString() == selection ? true : false })

		if (selectedPayee && selectedPayee.length > 0) {
			newFormFields.selectedPayee = selection;
			newFormFields.payeeId = selectedPayee[0].entityId.toString();
			newFormFields.payeeType = selectedPayee[0].entityType;
		}

		setFormFields(newFormFields);
	}

	const handleSubmit = async () => {
		setErrorMessage(undefined);
		setSuccessMessage(undefined);
		if (formFields.invoiceNumber.trim() === '' || formFields.month === '-1' || formFields.selectedPayee === '-1') {
			setErrorMessage("Please enter values for all fields to continue.");
			return;
		}

		setIsSaving(true);
		const { selectedPayee, ...batch } = formFields;
		BatchService.setDetails(batch)
			.then((response) => {
				console.log('BatchService.setDetails: SUCCESS', response);
				if (updateBatchDetails) {
					updateBatchDetails(response.data);
				};
				setSuccessMessage("Batch details saved successfully.");
				setTimeout(() => {
					setSuccessMessage(undefined);
				}, AppConfig.editSuccessToastMs);
			})
			.catch((error) => {
				console.log('BatchService.setDetails: ERROR', error);
				setErrorMessage("We encountered an error while saving the batch. Please try again.");
			})
			.finally(() => {
				setIsSaving(false);
			});
	}

	const handleDelete = async () => {
		if (await
			UtilityService.ConfirmationModal(
				confirm,
				"Delete Batch",
				<>
					<p>
						Deleting this batch will:
						<ol>
							<li>Remove the shipments</li>
							<li>Set each shipment's status to Reconciling</li>
							<li>Delete the batch</li>
						</ol>

						Would you like to continue?
					</p>
				</>,
				{ confirmationText: "Continue", confirmationButtonProps: { variant: 'contained' }, cancellationButtonProps: { color: 'error', variant: 'contained' } }
			) == false) {
			return;
		}

		setIsDeleting(true);
		BatchService.deleteBatch(formFields.id)
			.then((response) => {
				console.log('BatchService.deleteBatch: SUCCESS', response);
				navigate('/batch');
			})
			.catch((error) => {
				console.log('BatchService.deleteBatch: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
				setIsDeleting(false);
			})
	}

	return (
		<section className="hwi-batch-details-form">
			<Spinner dependency={isLoadingPayees || isLoadingDetails} text='Loading Payees...'>
				{errorMessage &&
					<Alert className="alert-error"
						severity="error"
						action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>
						{errorMessage}
					</Alert>
				}
				{successMessage &&
					<Alert className="alert-success"
						severity="success"
						action={<IconButton onClick={() => { setSuccessMessage(undefined); }} size='small'>X</IconButton>}>
						{successMessage}
					</Alert>
				}
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<Form onSubmit={() => handleSubmit()}>
						<div className="row">
							<InputLabel>Invoice Number *</InputLabel>
							<TextField
								className="invoice-field"
								disabled={!canEdit || isSaving}
								required
								variant="outlined"
								value={formFields ? formFields.invoiceNumber : ''}
								size="small"
								onChange={(event) => { setFormFields({ ...formFields, ["invoiceNumber"]: event.target.value }) }}>
							</TextField>
						</div>
						<div className="row">
							<InputLabel>Payee *</InputLabel>
							<Select
								className="payee-field"
								disabled={!canEdit || isSaving}
								value={formFields ? formFields.selectedPayee : '-1'}
								required
								fullWidth
								onChange={(event) => { setPayeeInfo(event.target.value) }}
							>
								<MenuItem value="-1" key={0} disabled>Select one...</MenuItem>
								{payeeOptions.map((item, index) => (
									<MenuItem value={item.localId} key={index} >{item.entityName} ({item.entityType})</MenuItem>
								))}
							</Select>
						</div>
						<div className="row">
							<InputLabel>Month *</InputLabel>
							<div className="date-field-container">
								<Select
									className="month-field"
									value={selectedMonthOption ?? '-1'}
									required
									fullWidth
									onChange={(event) => { setSelectedMonthOption(event.target.value) }}
								>
									<MenuItem value="-1" key={0} disabled>Select one...</MenuItem>
									{monthOptions.map((item, index) => (
										<MenuItem value={item.value} key={index} >{item.name}</MenuItem>
									))}
								</Select>
								<Select
									className="year-field"
									value={selectedYearOption ?? '-1'}
									required
									fullWidth
									onChange={(event) => { setSelectedYearOption(event.target.value) }}
								>
									<MenuItem value="-1" key={0} disabled>Select one...</MenuItem>
									{yearOptions.map((item, index) => (
										<MenuItem value={item.value} key={index} >{item.name}</MenuItem>
									))}
								</Select>
							</div>
						</div>
						<div className="row controls">
							<Button type="submit" className="save-button" variant="contained" disabled={!canEdit || isSaving || isDeleting || formFields.selectedPayee == "-1" || formFields.month == "-1" || formFields.invoiceNumber == ""}>{isSaving ? "Saving..." : "Save"}</Button>
							<Button type="button" className="delete-button" variant="contained" color="error" disabled={!canEdit || isSaving || isDeleting} onClick={handleDelete}>{isDeleting ? "Deleting..." : "Delete"}</Button>
						</div>
						{/* <Json data={formFields} title='Form Fields'></Json> */}
					</Form>
				</LocalizationProvider>
			</Spinner>
		</section>
	)
}

export default BatchDetailsForm;